import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabaseQuery } from './lib/supabaseQuery';
import { queryKeys } from './lib/queryKeys';

/**
 * Hook to count unread notifications mentioned for the current user.
 *
 * Server state via TanStack Query — the key starts with 'notifications', so
 * the centralized realtimeStore invalidations refresh the count live. (The
 * old useState + mount-only fetch never updated after the Firestore
 * migration: realtimeStore invalidated a query cache this hook never read.)
 *
 * Query key uses PRIMITIVES only (name/role/tenantId). Callers pass a fresh
 * object prop (e.g. `{ ...user }`) — keying on the object itself created a
 * new query key every render and caused an infinite refetch storm (the
 * network tab filled with repeated notifications requests).
 *
 * Intent: Provide real-time feedback on pending alerts by checking multiple name variations
 * (full name, first name, surname-prefix, and role) against the 'mentionedName' field in Supabase.
 *
 * @param {Object} currentUser - The currently authenticated user object.
 * @param {string} currentUser.name - The full name of the user.
 * @param {string} [currentUser.role] - The role of the user (e.g. 'admin', 'housekeeping').
 * @param {string} [currentUser.tenantId] - The active tenant id.
 * @returns {number} The count of notifications marked as 'read: false'.
 */
export function useUnreadNotifications(currentUser: { name?: string; role?: string; tenantId?: string } | null) {
  const currentUserName = currentUser?.name;
  const currentUserRole = currentUser?.role;
  const activeTenantId = currentUser?.tenantId;

  // Mention-name variants this user can be reached under (stable via useMemo —
  // destructured primitives only, so the query key doesn't churn every render).
  const possibleNames = useMemo(() => {
    if (!currentUserName) return [];
    const nameLower = currentUserName.toLowerCase();
    const nameSpace = currentUserName.split(" ")[0].toLowerCase();
    const nameDot = currentUserName.split(".")[0].toLowerCase();
    const roleLower = (currentUserRole || "").toLowerCase();
    return [...new Set([nameDot, nameLower, nameSpace, roleLower])].filter(Boolean);
  }, [currentUserName, currentUserRole]);

  const { data } = useQuery({
    queryKey: queryKeys.notifications.unread(activeTenantId ?? 'unknown'),
    queryFn: supabaseQuery(db => db.from('notifications')
      .select('id, read, mentioned_name')
      .eq('tenant_id', activeTenantId)
      .in('mentioned_name', possibleNames)
      .limit(50)),
    enabled: !!currentUserName && !!activeTenantId && possibleNames.length > 0,
  });

  // Derive the count from the stable data reference in render (useMemo) —
  // never inside a select that builds new arrays/objects per call.
  const unreadCount = useMemo(() => {
    if (!data) return 0;
    return data.filter(n => !n.read && !!n.mentioned_name && possibleNames.includes(n.mentioned_name)).length;
  }, [data, possibleNames]);

  return unreadCount;
}

/**
 * Hook to dynamically display a notification count in the browser tab title
 * and draw a red badge over the page's favicon.
 *
 * @param {number} unreadCount - The number of unread notifications.
 */
export function useBrowserTabNotification(unreadCount: number) {
  useEffect(() => {
    // 1. Update Document Title
    const originalTitle = "AdaptHub";
    document.title = unreadCount > 0 ? `(${unreadCount}) ${originalTitle}` : originalTitle;

    // 2. Update Favicon Badge
    const favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!favicon) return;

    if (unreadCount === 0) {
      favicon.href = "/favicon.png";
      return;
    }

    const img = new Image();
    img.src = "/favicon.png";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Draw original favicon
      ctx.drawImage(img, 0, 0, 32, 32);

      // Draw red badge circle
      ctx.fillStyle = "#EF4444"; // Tailwind's red-500 HSL/Hex equivalent
      ctx.beginPath();
      ctx.arc(24, 8, 8, 0, 2 * Math.PI);
      ctx.fill();

      // Draw badge count text
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "bold 9px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const displayText = unreadCount > 9 ? "9+" : String(unreadCount);
      ctx.fillText(displayText, 24, 8);

      favicon.href = canvas.toDataURL("image/png");
    };
  }, [unreadCount]);
}
